/**
 * Watch a host that is not in the config.
 *
 * The site picker is not cosmetic: it decides the default health path and *which* API
 * key the backend would consider for this host. Whether that key actually travels is
 * the backend's decision alone - it is sent only to hosts matching that site's trusted
 * suffixes - so filing an unrelated host under a site gains it nothing, and the form
 * says as much rather than letting an operator assume otherwise.
 *
 * A refused host is shown with the backend's reason. Silently dropping it would make a
 * typo look like a host that is simply never checked.
 */

import { ChevronDown, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import type {
  ApiKeyEndpointConfig,
  ManualApiKeyConfig,
  ManualTargetRequest,
  SiteInfo,
  TargetResult,
} from '../api/types'
import { cn } from '../lib/cn'
import { STATUS_PRESENTATION, statusText } from '../lib/status'
import {
  EMPTY_ENDPOINT,
  EndpointFieldset,
  buildEndpoint,
  inputClass,
} from './ui/EndpointFields'
import type { EndpointFields } from './ui/EndpointFields'

interface ManualTargetFormProps {
  sites: SiteInfo[]
  onSubmit: (target: ManualTargetRequest) => void
  isPending: boolean
  /** Reasons the backend gave for refusing hosts, from the last probe. */
  rejected: Array<{ target: string; reason: string }>
  /** Manual monitors currently being watched, from the unfiltered result set. */
  monitors: TargetResult[]
  onRemove: (targetId: string) => void
  removingId: string | null
  removeError: string | null
}

export function ManualTargetForm({
  sites,
  onSubmit,
  isPending,
  rejected,
  monitors,
  onRemove,
  removingId,
  removeError,
}: ManualTargetFormProps) {
  const [siteId, setSiteId] = useState(sites[0]?.id ?? '')
  const [host, setHost] = useState('')
  const [label, setLabel] = useState('')
  const [path, setPath] = useState('')
  const [scheme, setScheme] = useState<'http' | 'https'>('https')

  // Detailed-mode (API key) fields, behind a disclosure - most manual hosts only need
  // the public view, so the key lifecycle is opt-in rather than always on screen.
  const [showKey, setShowKey] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [keyHeader, setKeyHeader] = useState('')
  const [issue, setIssue] = useState<EndpointFields>({ ...EMPTY_ENDPOINT })
  const [verify, setVerify] = useState<EndpointFields>({ ...EMPTY_ENDPOINT, method: 'GET' })
  const [keyError, setKeyError] = useState<string | null>(null)

  const site = sites.find((entry) => entry.id === siteId)

  const resetForm = () => {
    setHost('')
    setLabel('')
    setApiKey('')
    setIssue({ ...EMPTY_ENDPOINT })
    setVerify({ ...EMPTY_ENDPOINT, method: 'GET' })
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    setKeyError(null)
    const trimmed = host.trim()
    if (!trimmed) return

    // Assemble the optional key config. Any of an explicit key, a header, or an
    // issue/verify endpoint turns detailed mode on for this host.
    let apiKeyConfig: ManualApiKeyConfig | undefined
    if (showKey) {
      let issueCfg: ApiKeyEndpointConfig | null
      let verifyCfg: ApiKeyEndpointConfig | null
      try {
        issueCfg = buildEndpoint(issue, 'issue')
        verifyCfg = buildEndpoint(verify, 'verify')
      } catch (err) {
        setKeyError((err as Error).message)
        return
      }
      if (keyHeader.trim() || issueCfg || verifyCfg) {
        apiKeyConfig = {
          ...(keyHeader.trim() ? { api_key_header: keyHeader.trim() } : {}),
          ...(issueCfg ? { issue_api_key: issueCfg } : {}),
          ...(verifyCfg ? { verify_api_key: verifyCfg } : {}),
        }
      }
    }

    onSubmit({
      site_id: siteId,
      host: trimmed,
      path: path.trim() || null,
      scheme,
      ...(label.trim() ? { label: label.trim() } : {}),
      ...(showKey && apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      ...(apiKeyConfig ? { api_key_config: apiKeyConfig } : {}),
    })
    resetForm()
  }

  return (
    <section className="card p-4" aria-label="Check another host">
      <h2 className="text-sm font-semibold text-ink">Check another host</h2>
      <p className="mt-1 text-xs text-ink-secondary">
        Enter a hostname, <code className="text-ink">host:port</code>, or a full URL. The
        site decides the default path and which key could apply.
      </p>

      <form onSubmit={submit} className="mt-3 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-ink-muted">Site</span>
          <select
            value={siteId}
            onChange={(event) => setSiteId(event.target.value)}
            className="rounded-md border bg-surface px-2 py-1.5 text-xs text-ink"
          >
            {sites.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-ink-muted">Scheme</span>
          <select
            value={scheme}
            onChange={(event) => setScheme(event.target.value as 'http' | 'https')}
            className="rounded-md border bg-surface px-2 py-1.5 text-xs text-ink"
          >
            <option value="https">https</option>
            <option value="http">http</option>
          </select>
        </label>

        <label className="flex min-w-[14rem] flex-1 flex-col gap-1">
          <span className="text-[11px] text-ink-muted">Host</span>
          <input
            value={host}
            onChange={(event) => setHost(event.target.value)}
            placeholder="staging.whitehawk.io"
            autoComplete="off"
            spellCheck={false}
            className="rounded-md border bg-surface px-2 py-1.5 text-xs text-ink placeholder:text-ink-muted"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-ink-muted">Path</span>
          <input
            value={path}
            onChange={(event) => setPath(event.target.value)}
            placeholder={site?.path ?? '/api/health'}
            autoComplete="off"
            spellCheck={false}
            className="w-32 rounded-md border bg-surface px-2 py-1.5 text-xs text-ink placeholder:text-ink-muted"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-ink-muted">Label</span>
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Manual"
            autoComplete="off"
            className="w-32 rounded-md border bg-surface px-2 py-1.5 text-xs text-ink placeholder:text-ink-muted"
          />
        </label>

        <button
          type="submit"
          disabled={isPending || !host.trim()}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium',
            'text-ink transition-colors hover:bg-surface-sunken disabled:opacity-60',
          )}
        >
          <Plus size={13} aria-hidden />
          {isPending ? 'Probing…' : 'Probe'}
        </button>
      </form>

      {/* Detailed mode is opt-in for a manual host: give it a key, or the endpoints that
          issue and verify one, and it can show the authenticated view like a configured
          site. The verify call sends the key in this header. */}
      <button
        type="button"
        onClick={() => setShowKey((v) => !v)}
        aria-expanded={showKey}
        className="mt-3 inline-flex items-center gap-1 text-xs text-ink-secondary hover:text-ink"
      >
        <ChevronDown
          size={13}
          aria-hidden
          className={cn('transition-transform', showKey && 'rotate-180')}
        />
        API key (for detailed mode)
      </button>

      {showKey ? (
        <div className="mt-2 space-y-3 rounded-md border p-3">
          <p className="text-[11px] text-ink-muted">
            Optional. Paste a key to use directly, and/or give the endpoints that issue and
            verify one. A payload may contain{' '}
            <code className="text-ink-secondary">{'{api_key}'}</code>, substituted at call
            time. The verify call sends the key in the header below.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-ink-muted">API key (optional)</span>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="paste an existing key, or leave blank to issue one"
                autoComplete="off"
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-ink-muted">Key header</span>
              <input
                value={keyHeader}
                onChange={(e) => setKeyHeader(e.target.value)}
                placeholder={site?.path ? 'X-API-Key' : 'X-API-Key'}
                autoComplete="off"
                className={inputClass}
              />
            </label>
          </div>

          <EndpointFieldset title="Issue endpoint" fields={issue} onChange={setIssue} showKeyField />
          <EndpointFieldset title="Verify endpoint" fields={verify} onChange={setVerify} />

          {keyError ? <p className="text-xs text-critical">{keyError}</p> : null}
        </div>
      ) : null}

      {site ? (
        <p className="mt-2 text-[11px] text-ink-muted">
          {site.has_api_key ? (
            <>
              A key is configured for {site.name} (from{' '}
              <code className="text-ink-secondary">{site.api_key_source}</code>) and will be
              sent only to{' '}
              {site.trusted_host_suffixes.map((suffix, index) => (
                <span key={suffix}>
                  {index > 0 ? ', ' : ''}
                  <code className="text-ink-secondary">*.{suffix}</code>
                </span>
              ))}
              . Any other host gets the public view.
            </>
          ) : (
            <>
              No API key is configured for {site.name}, so every host filed under it
              returns the public view. Set one of{' '}
              {site.api_key_env.map((name, index) => (
                <span key={name}>
                  {index > 0 ? ', ' : ''}
                  <code className="text-ink-secondary">{name}</code>
                </span>
              ))}
              .
            </>
          )}
        </p>
      ) : null}

      {rejected.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {rejected.map((entry) => (
            <li key={entry.target} className="text-xs text-critical">
              <span className="font-medium">{entry.target}</span>{' '}
              <span className="text-ink-secondary">{entry.reason}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/*
        Listed here as well as on each card, because a monitor hidden by the site filter
        or by "problems only" still needs to be removable - a card is not a reliable
        place to put the only copy of a control.
      */}
      {monitors.length > 0 ? (
        <div className="mt-4 border-t pt-3">
          <h3 className="text-xs font-semibold text-ink-secondary">
            Watching {monitors.length} manual host{monitors.length === 1 ? '' : 's'}
          </h3>
          <p className="mt-0.5 text-[11px] text-ink-muted">
            Polled on the same interval as the configured targets, and remembered across
            a restart.
          </p>

          <ul className="mt-2 divide-y">
            {monitors.map((monitor) => {
              const presentation = STATUS_PRESENTATION[monitor.status]
              const Icon = presentation.icon
              return (
                <li
                  key={monitor.target_id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <span className="flex min-w-0 items-center gap-2 text-xs">
                    <Icon
                      size={13}
                      aria-hidden
                      className={cn('shrink-0', statusText(presentation.token))}
                    />
                    <span className="truncate text-ink">{monitor.host}</span>
                    <span className="shrink-0 text-ink-muted">
                      {monitor.site_name} · {presentation.label}
                    </span>
                  </span>

                  <button
                    type="button"
                    onClick={() => onRemove(monitor.target_id)}
                    disabled={removingId === monitor.target_id}
                    aria-label={`Stop watching ${monitor.host}`}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[11px] text-ink-secondary transition-colors hover:bg-surface-sunken hover:text-critical disabled:opacity-60"
                  >
                    <Trash2 size={12} aria-hidden />
                    {removingId === monitor.target_id ? 'Removing…' : 'Remove'}
                  </button>
                </li>
              )
            })}
          </ul>

          {removeError ? (
            <p className="mt-2 text-xs text-critical">{removeError}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
